import {
    Button,
    Modal,
    Notifications,
    Progress,
    Select,
} from "d-react-components";
import { useFormik } from "formik";
import { findIndex, map } from "lodash";
import React, { useContext, useMemo, useState } from "react";
import OrderAPI from "../../api/order/OrderAPI";
import AddressFormInput from "../../components/AddressFormInput";
import InputTextForm from "../../components/InputTextForm";
import SectionHeader from "../../components/SectionHeader";
import { ORDER_STATUS } from "../../constant/order";
import { AppStateContext } from "../../context/app";
import {
    getFullAddress,
    IServiceAddress,
    mapAddressToSer,
} from "../../interface/address";
import Messages from "../../languages/Messages";
import { AddressServiceSchema } from "../../schema/address";

interface IAddressUpdateModal {
    open: boolean;
    onClose: () => void;
}

interface IAddressDetail {
    address: IServiceAddress;
    index: number;
}

const AddressUpdateModal = ({ open, onClose }: IAddressUpdateModal) => {
    const { orderDetail, setOrderDetail } = useContext(AppStateContext);
    const { shippings = [], status } = orderDetail ?? {};

    const defaultFormValue = {
        index: 1,
        ...(shippings[0] ?? {}),
    };
    const addressForm = useFormik<any>({
        initialValues: defaultFormValue as any,
        validateOnChange: false,
        validateOnBlur: false,
        validationSchema: AddressServiceSchema,
        onSubmit: (values: any) => {
            const input = mapAddressToSer(values);
            onUpdateShippingAddress(values?.id, input);
        },
    });

    const onUpdateShippingAddress = (addressId: string, input: any) => {
        Progress.show(
            {
                method: OrderAPI.updateServiceAddress,
                params: [orderDetail?.id, addressId, input],
            },
            (orderRes) => {
                onClose();
                setOrderDetail(orderRes);
                Notifications.showSuccess(
                    Messages.updateShippingAddressSuccess
                );
            }
        );
    };

    const onChangeAddress = (id: string) => {
        const index = findIndex(shippings, (item) => item.id === id);
        onUpdateAddress(shippings[index], index);
    };

    const onUpdateAddress = (shipping: IServiceAddress, index: number) => {
        addressForm.setValues({ ...shipping, index });
    };

    const classNameInput = "col-12 col-sm-6 mt-3";
    const formValues = addressForm.values;
    const formErrors = addressForm.errors;
    const inputProps = {
        className: classNameInput,
        form: addressForm,
    };
    return (
        <Modal
            open={open}
            onClose={onClose}
            title={Messages.updateShippingAddress}
            onSave={() => addressForm.handleSubmit()}
        >
            <div className="row">
                <Select
                    {...inputProps}
                    className="col-12 mt-3"
                    dataSource={shippings ?? []}
                    getLabel={getFullAddress}
                    value={formValues.id}
                    onChange={onChangeAddress}
                />
                <InputTextForm {...inputProps} keyData="firstName" />
                <InputTextForm {...inputProps} keyData="lastName" />
                <InputTextForm {...inputProps} keyData="nickName" />
                <InputTextForm {...inputProps} keyData="phone" />
                <AddressFormInput
                    formDataError={formErrors}
                    formDataValue={formValues}
                    onChange={(values: any) => {
                        addressForm.setValues({
                            ...addressForm.values,
                            ...values,
                        });
                    }}
                    show={{ country: false }}
                />
            </div>
        </Modal>
    );
};

const AddressDetail = ({ address, index }: IAddressDetail) => {
    const { orderDetail } = useContext(AppStateContext);
    const { status } = orderDetail ?? {};
    const { firstName, lastName, phone } = address;
    const [openEdit, setOpenEdit] = useState(false);
    const isHideEdit = useMemo(
        () =>
            status !== ORDER_STATUS.PENDING_PAYMENT &&
            status !== ORDER_STATUS.PENDING_CONFIRMATION,
        [status]
    );

    const fullAddress = getFullAddress(address);

    return (
        <div className="flex-column mt-3">
            <div className="d-flex justify-content-between align-items-center">
                <label>{`${Messages.address} ${index}`}</label>
                <Button
                    onClick={() => setOpenEdit(true)}
                    variant="trans"
                    hidden={isHideEdit}
                >
                    {Messages.edit}
                </Button>
            </div>
            <div>{`${firstName} ${lastName}`}</div>
            <div>{phone}</div>
            <div>{fullAddress}</div>
            {openEdit && (
                <AddressUpdateModal
                    open={openEdit}
                    onClose={() => setOpenEdit(false)}
                />
            )}
        </div>
    );
};

const OrderShippingAddress = () => {
    const { orderDetail, setOrderDetail } = useContext(AppStateContext);
    const { shippings = [] } = orderDetail ?? {};

    return (
        <div className="card-container p-4 mt-3">
            <SectionHeader label={Messages.shippingInformation} hiddenEdit />
            <div className="flex-column">
                {map(shippings, (address, index) => (
                    <AddressDetail address={address} index={index + 1} />
                ))}
            </div>
        </div>
    );
};

export default OrderShippingAddress;
